/**
 * Alerts page (Phase 24 — Alerts & Notification Foundation).
 *
 * Lists all monitoring alerts for the org.
 * Allows marking individual alerts or all as read.
 *
 * API endpoints used:
 *   GET  /alerts              — paginated alert list
 *   PATCH /alerts/:id/read   — mark single alert as read
 *   POST  /alerts/read-all   — mark all as read
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, ApiError, friendlyError } from '../lib/api-client';
import { useAuth } from '../hooks/useAuth';
import i18n from '../i18n/i18n';
import { formatDateTime } from '../i18n/formatters';
import LockedFeatureState from '../components/entitlements/LockedFeatureState';
import Card from '../components/ui/Card';
import Container from '../components/ui/Container';
import Button from '../components/ui/Button';
import Badge, { BadgeVariant } from '../components/ui/Badge';
import PageHeader from '../components/ui/PageHeader';
import StatCard from '../components/ui/StatCard';
import type {
  Alert,
  AlertPreferenceMap,
  AlertPreferenceProfile,
  AlertSeverity,
  AlertVisibilityClass,
} from '@accestra/types';

// -------------------------------------------------------------------
// Types
// -------------------------------------------------------------------

interface AlertsResponse {
  alerts: Alert[];
  unreadCount: number;
  pagination: { total: number; page: number; pageSize: number };
}

interface OrgUsageResponse {
  plan: string;
  limits: {
    alertsEnabled: boolean;
  };
}

interface AlertPreferencesResponse {
  preferences: AlertPreferenceProfile;
}

const ALERTS_UPGRADE_PATH = '/billing?plan=starter&source=alerts-lock';
// -------------------------------------------------------------------
// Design tokens
// -------------------------------------------------------------------

const C = {
  page: 'var(--acc-color-page)',
  text: 'var(--acc-color-text)',
  muted: 'var(--acc-color-text-muted)',
  soft: 'var(--acc-color-text-soft)',
  link: 'var(--acc-color-primary)',
  border: 'var(--acc-color-border)',
  surface: 'var(--card-bg)',
  surfaceMuted: 'var(--surface-secondary)',
  warning: 'var(--acc-color-warning)',
  warningSoft: 'var(--acc-color-warning-soft)',
  danger: 'var(--acc-color-danger)',
  success: 'var(--acc-color-success)',
};

// -------------------------------------------------------------------
// Severity → badge variant
// -------------------------------------------------------------------

function severityVariant(severity: AlertSeverity): BadgeVariant {
  switch (severity) {
    case 'critical': return 'danger';
    case 'warning':  return 'warning';
    default:         return 'info';
  }
}

// -------------------------------------------------------------------
// Alert type label
// -------------------------------------------------------------------

function alertTypeLabel(type: string): string {
  switch (type) {
    case 'score_drop':         return i18n.t('alerts:types.score_drop');
    case 'new_critical_issues': return i18n.t('alerts:types.new_critical_issues');
    case 'issue_regression':   return i18n.t('alerts:types.issue_regression');
    case 'issue_reappeared':   return i18n.t('alerts:types.issue_reappeared');
    case 'scan_failed':        return i18n.t('alerts:types.scan_failed');
    case 'scan_completed':     return i18n.t('alerts:types.scan_completed');
    default:                   return type.replace(/_/g, ' ');
  }
}

function eventClassLabel(metadata: Record<string, unknown>): string | null {
  switch (metadata['eventClass']) {
    case 'auth_operational': return i18n.t('alerts:eventClass.auth_operational');
    case 'scan_operational': return i18n.t('alerts:eventClass.scan_operational');
    case 'accessibility_regression': return i18n.t('alerts:eventClass.accessibility_regression');
    case 'normal_scan': return i18n.t('alerts:eventClass.normal_scan');
    default: return null;
  }
}

function visibilityClassVariant(value: AlertVisibilityClass): BadgeVariant {
  switch (value) {
    case 'operational': return 'warning';
    case 'compliance_risk': return 'danger';
    case 'informational':
    default:
      return 'info';
  }
}

function visibilityClassLabel(value: AlertVisibilityClass): string {
  switch (value) {
    case 'informational': return i18n.t('alerts:visibility.informational');
    case 'operational': return i18n.t('alerts:visibility.operational');
    case 'compliance_risk': return i18n.t('alerts:visibility.compliance_risk');
    default: return i18n.t('alerts:visibility.informational');
  }
}

function preferenceLabel(key: keyof AlertPreferenceMap, t: ReturnType<typeof useTranslation>['t']): string {
  switch (key) {
    case 'scanCompleted': return t('preferences.labels.scanCompleted');
    case 'scanFailed': return t('preferences.labels.scanFailed');
    case 'authOperationalFailure': return t('preferences.labels.authOperationalFailure');
    case 'issueRegression': return t('preferences.labels.issueRegression');
    case 'issueReappeared': return t('preferences.labels.issueReappeared');
    default: return key;
  }
}

// -------------------------------------------------------------------
// Page component
// -------------------------------------------------------------------

export default function AlertsPage() {
  const { t } = useTranslation('alerts');
  const navigate = useNavigate();
  const { role } = useAuth();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [markingAll, setMarkingAll] = useState(false);
  const [alertsEnabled, setAlertsEnabled] = useState(true);
  const [preferences, setPreferences] = useState<AlertPreferenceProfile | null>(null);
  const [preferenceDraft, setPreferenceDraft] = useState<AlertPreferenceMap | null>(null);
  const [savingPreferences, setSavingPreferences] = useState(false);
  const [preferenceFeedback, setPreferenceFeedback] = useState<{
    message: string;
    tone: 'success' | 'danger';
  } | null>(null);
  const alertsBenefits = useMemo(() => ([
    {
      title: t('locked.benefits.scanFailures.title'),
      body: t('locked.benefits.scanFailures.body'),
    },
    {
      title: t('locked.benefits.scoreDrops.title'),
      body: t('locked.benefits.scoreDrops.body'),
    },
    {
      title: t('locked.benefits.teamRisk.title'),
      body: t('locked.benefits.teamRisk.body'),
    },
  ]), [t]);

  const loadAlerts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const usage = await api.get<OrgUsageResponse>('/orgs/me/usage');
      const enabled = usage.limits.alertsEnabled;
      setAlertsEnabled(enabled);

      if (!enabled) {
        setAlerts([]);
        setUnreadCount(0);
        setPreferences(null);
        setPreferenceDraft(null);
        return;
      }

      const [data, preferenceData] = await Promise.all([
        api.get<AlertsResponse>('/alerts?pageSize=50'),
        api.get<AlertPreferencesResponse>('/alerts/preferences'),
      ]);
      setAlerts(data.alerts ?? []);
      setUnreadCount(data.unreadCount ?? 0);
      setPreferences(preferenceData.preferences ?? null);
      setPreferenceDraft(preferenceData.preferences?.organisationDefaults ?? null);
    } catch (err) {
      if (err instanceof ApiError && err.statusCode === 403) {
        setAlertsEnabled(false);
        setAlerts([]);
        setUnreadCount(0);
        setPreferences(null);
        setPreferenceDraft(null);
        setError(null);
        return;
      }
      setError(friendlyError(err, t('feedback.loadFailed')));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  const handleMarkRead = async (alertId: string) => {
    try {
      await api.patch(`/alerts/${alertId}/read`, {});
      setAlerts((prev) =>
        prev.map((a) => (a.id === alertId ? { ...a, isRead: true } : a)),
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {
      // Ignore transient errors
    }
  };

  const handleMarkAllRead = async () => {
    setMarkingAll(true);
    try {
      await api.post('/alerts/read-all', {});
      setAlerts((prev) => prev.map((a) => ({ ...a, isRead: true })));
      setUnreadCount(0);
    } catch (err) {
      setError(friendlyError(err, t('feedback.markAllFailed')));
    } finally {
      setMarkingAll(false);
    }
  };

  const canManagePreferences = role === 'owner' || role === 'admin';

  const handlePreferenceChange = (key: keyof AlertPreferenceMap, value: boolean) => {
    setPreferenceDraft((prev) => ({
      scanCompleted: prev?.scanCompleted ?? preferences?.organisationDefaults.scanCompleted ?? true,
      scanFailed: prev?.scanFailed ?? preferences?.organisationDefaults.scanFailed ?? true,
      authOperationalFailure: prev?.authOperationalFailure ?? preferences?.organisationDefaults.authOperationalFailure ?? true,
      issueRegression: prev?.issueRegression ?? preferences?.organisationDefaults.issueRegression ?? true,
      issueReappeared: prev?.issueReappeared ?? preferences?.organisationDefaults.issueReappeared ?? true,
      [key]: value,
    }));
  };

  const handleSavePreferences = async () => {
    if (!preferenceDraft) return;
    setSavingPreferences(true);
    setPreferenceFeedback(null);
    try {
      const data = await api.patch<AlertPreferencesResponse>('/alerts/preferences', preferenceDraft);
      setPreferences(data.preferences ?? null);
      setPreferenceDraft(data.preferences?.organisationDefaults ?? null);
      setPreferenceFeedback({ message: t('feedback.preferencesSaved'), tone: 'success' });
    } catch {
      setPreferenceFeedback({ message: t('feedback.preferencesSaveFailed'), tone: 'danger' });
    } finally {
      setSavingPreferences(false);
    }
  };

  const tbl = {
    table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 13 },
    th: {
      textAlign: 'left' as const,
      padding: '13px 16px',
      background: C.surfaceMuted,
      borderBottom: `1px solid ${C.border}`,
      color: C.soft,
      fontWeight: 760,
      fontSize: 10,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.12em',
    },
    td: {
      padding: '14px 16px',
      borderBottom: `1px solid ${C.border}`,
      verticalAlign: 'top' as const,
    },
  };

  const operationalCount = alerts.filter((alert) => alert.visibilityClass === 'operational').length;
  const complianceRiskCount = alerts.filter((alert) => alert.visibilityClass === 'compliance_risk').length;
  const informationalCount = alerts.filter((alert) => alert.visibilityClass === 'informational').length;

  return (
    <Container maxWidth={1120}>

        <div style={{ marginBottom: 24 }}>
          <PageHeader
            eyebrow={t('header.eyebrow')}
            title={t('header.title')}
            subtitle={unreadCount > 0 ? t('header.subtitleUnread', { count: unreadCount }) : t('header.subtitleClear')}
            description={t('header.description')}
            meta={
              alertsEnabled && !loading ? (
                <>
                  <Badge variant={unreadCount > 0 ? 'warning' : 'success'}>
                    {unreadCount > 0 ? t('meta.unread', { count: unreadCount }) : t('meta.noUnread')}
                  </Badge>
                  <Badge variant={complianceRiskCount > 0 ? 'danger' : 'neutral'}>
                    {t('meta.complianceRisk', { count: complianceRiskCount })}
                  </Badge>
                  <Badge variant={operationalCount > 0 ? 'warning' : 'neutral'}>
                    {t('meta.operational', { count: operationalCount })}
                  </Badge>
                </>
              ) : undefined
            }
            action={
              unreadCount > 0 ? (
                <Button
                  variant="secondary"
                  onClick={handleMarkAllRead}
                  disabled={markingAll}
                >
                  {markingAll ? t('actions.marking') : t('actions.markAllRead')}
                </Button>
              ) : undefined
            }
          />
        </div>

        {alertsEnabled && !loading && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: 14,
              marginBottom: 20,
            }}
          >
            <StatCard value={unreadCount} label={t('stats.unread')} />
            <StatCard value={operationalCount} label={t('stats.operational')} accent={operationalCount > 0 ? C.warning : undefined} />
            <StatCard value={complianceRiskCount} label={t('stats.complianceRisk')} accent={complianceRiskCount > 0 ? C.danger : undefined} />
            <StatCard value={informationalCount} label={t('stats.informational')} />
          </div>
        )}

        {error && alertsEnabled && (
          <div style={{ color: C.danger, fontSize: 13, marginBottom: 16 }}>{error}</div>
        )}

        {!loading && !alertsEnabled && (
          <LockedFeatureState
            tierLabel={t('locked.tier')}
            title={t('locked.title')}
            description={t('locked.description')}
            benefits={alertsBenefits}
            upgradeLabel={t('locked.upgradeLabel')}
            onUpgrade={() => navigate(ALERTS_UPGRADE_PATH)}
            onPricing={() => navigate('/pricing')}
          />
        )}

        {alertsEnabled && (
          <>
            {preferences && preferenceDraft && (
              <Card style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 16 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 4 }}>
                      {t('preferences.title')}
                    </div>
                    <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
                      {t('preferences.description')}
                    </div>
                  </div>
                  <Badge variant="info">{t('preferences.inApp')}</Badge>
                </div>

                <div className="acc-alert-preferences">
                  {(Object.keys(preferenceDraft) as Array<keyof AlertPreferenceMap>).map((key) => (
                    <label
                      key={key}
                      className="acc-alert-preference-row"
                    >
                      <input
                        type="checkbox"
                        checked={preferenceDraft[key]}
                        disabled={!canManagePreferences}
                        onChange={(event) => handlePreferenceChange(key, event.target.checked)}
                      />
                      <span className="acc-alert-preference-row__copy">
                        <span className="acc-alert-preference-row__title">
                          {preferenceLabel(key, t)}
                        </span>
                        <span className="acc-alert-preference-row__hint">
                          {preferenceDraft[key] ? t('preferences.visible') : t('preferences.muted')}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>

                {preferenceFeedback && (
                  <div style={{ color: preferenceFeedback.tone === 'danger' ? C.danger : C.success, fontSize: 12, marginBottom: 10 }}>
                    {preferenceFeedback.message}
                  </div>
                )}

                {canManagePreferences ? (
                  <Button onClick={handleSavePreferences} disabled={savingPreferences}>
                    {savingPreferences ? t('actions.savingPreferences') : t('actions.savePreferences')}
                  </Button>
                ) : (
                  <div style={{ fontSize: 12, color: C.muted }}>
                    {t('preferences.ownerAdminOnly')}
                  </div>
                )}
              </Card>
            )}

            <Card style={{ padding: 0, overflow: 'hidden' }}>
              {loading && (
                <p style={{ color: C.muted, fontSize: 13, padding: '16px 24px', margin: 0 }}>
                  {t('common:actions.loading')}
                </p>
              )}

              {!loading && alerts.length === 0 && (
                <div style={{ textAlign: 'center', padding: '56px 32px' }}>
                  <div style={{ fontSize: 40, marginBottom: 16, lineHeight: 1 }} aria-hidden="true">🔔</div>
                  <h3 style={{ margin: '0 0 8px', fontWeight: 700, fontSize: 18, color: C.text }}>
                    {t('empty.title')}
                  </h3>
                  <p style={{ margin: 0, color: C.muted, fontSize: 14, lineHeight: 1.65 }}>
                    {t('empty.description')}
                  </p>
                </div>
              )}

              {!loading && alerts.length > 0 && (
                <table style={tbl.table} className="acc-data-table acc-data-table--alerts">
                  <thead>
                    <tr>
                      <th style={tbl.th}>{t('table.severity')}</th>
                      <th style={tbl.th}>{t('table.class')}</th>
                      <th style={tbl.th}>{t('table.type')}</th>
                      <th style={tbl.th}>{t('table.title')}</th>
                      <th style={tbl.th}>{t('table.message')}</th>
                      <th style={tbl.th}>{t('table.date')}</th>
                      <th style={tbl.th}>{t('table.action')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {alerts.map((alert) => (
                      <tr
                        key={alert.id}
                        style={{ background: alert.isRead ? 'transparent' : C.warningSoft }}
                      >
                        <td style={tbl.td}>
                          <Badge variant={severityVariant(alert.severity)}>
                            {t(`severity.${alert.severity}` as const, { defaultValue: alert.severity })}
                          </Badge>
                        </td>
                        <td style={tbl.td}>
                          <Badge variant={visibilityClassVariant(alert.visibilityClass)}>
                            {visibilityClassLabel(alert.visibilityClass)}
                          </Badge>
                        </td>
                        <td style={{ ...tbl.td, color: C.muted, whiteSpace: 'nowrap' }}>
                          <div>{alertTypeLabel(alert.alertType)}</div>
                          {eventClassLabel(alert.metadata) && (
                            <div className="acc-table-meta" style={{ marginTop: 4 }}>
                              {eventClassLabel(alert.metadata)}
                            </div>
                          )}
                        </td>
                        <td style={{ ...tbl.td, fontWeight: alert.isRead ? 500 : 760, maxWidth: 220 }}>
                          {alert.siteId ? (
                            <Link to={`/sites/${alert.siteId}`} className="acc-table-primary-link">
                              {alert.title}
                            </Link>
                          ) : alert.title}
                        </td>
                        <td style={{ ...tbl.td, color: C.muted, maxWidth: 320, lineHeight: 1.5 }}>
                          {alert.message}
                          {alert.scanId && (
                            <div style={{ marginTop: 4 }}>
                              <Link to={`/scans/${alert.scanId}`} className="acc-table-tertiary-link">
                                {t('actions.viewScan')}
                              </Link>
                            </div>
                          )}
                        </td>
                        <td style={{ ...tbl.td, color: C.muted, whiteSpace: 'nowrap' }}>
                          {formatDateTime(alert.createdAt)}
                        </td>
                        <td style={tbl.td}>
                          {!alert.isRead ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleMarkRead(alert.id)}
                            >
                              {t('actions.dismiss')}
                            </Button>
                          ) : (
                            <span className="acc-table-meta">{t('actions.read')}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
          </>
        )}
    </Container>
  );
}
